import { useQuery } from "@tanstack/react-query";
import { listVoices } from "../api";
import { useT } from "../i18n";

/**
 * ModelPicker chooses the speech model a book is narrated in (ADR-0028).
 *
 * Options side by side rather than a list, because the choice is a listening
 * test between them: Gemini 3.1 Flash is the default, 2.5 Pro and Soniox
 * (ADR-0031) are offered for Persian. Like the voice, choosing saves on the book and re-narrates
 * nothing — the Narrate control is where a re-recording is announced.
 */
export default function ModelPicker({
  value,
  language,
  onChange,
  disabled = false,
}: {
  /** The book's chosen model, or "" for the installation default. */
  value: string;
  /**
   * The book's language. A model offered in some languages only is shown on a
   * book in one of them (ADR-0113) — the server refuses any other pairing, so
   * this shows the rule rather than keeping it.
   */
  language: string;
  onChange: (model: string) => void;
  disabled?: boolean;
}) {
  const t = useT();
  // The same constant catalogue the voice picker reads, from the same cache.
  const catalogue = useQuery({
    queryKey: ["voices"],
    queryFn: listVoices,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const all = catalogue.data?.models ?? [];
  const selected = value || catalogue.data?.default_model || all[0]?.name || "";
  const code = language.trim().toLowerCase();
  // The chosen model stays listed even if the book's language changed under
  // it, so the picker never shows nothing chosen.
  const models = all.filter(
    (m) => !m.languages?.length || m.languages.includes(code) || m.name === selected,
  );
  if (models.length < 2) return null;

  return (
    <div className="mt-3" data-testid="model-picker">
      <p className="mb-1.5 text-xs text-muted">{t.voice.modelLabel}</p>
      <div
        role="radiogroup"
        aria-label={t.voice.modelLabel}
        className="grid gap-1 rounded-control bg-sunk p-1"
        style={{
          // Side by side up to three; four or more as two columns, which
          // still fits a phone.
          gridTemplateColumns: `repeat(${models.length > 3 ? 2 : models.length}, minmax(0, 1fr))`,
        }}
      >
        {models.map((m) => (
          <button
            key={m.name}
            type="button"
            role="radio"
            aria-checked={selected === m.name}
            data-testid="model-option"
            data-model={m.name}
            disabled={disabled}
            onClick={() => {
              if (selected !== m.name) onChange(m.name);
            }}
            className={`rounded-[0.55rem] px-3 py-1.5 text-sm transition-colors disabled:opacity-50 ${
              selected === m.name ? "bg-surface font-medium text-ink" : "text-muted hover:text-ink"
            }`}
          >
            {t.voice.models[m.name] ?? m.label}
          </button>
        ))}
      </div>
    </div>
  );
}
